import React, { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { FiChevronDown, FiChevronUp, FiTrash2 } from "react-icons/fi";
import { Row, Col, Label, Input, Button } from "reactstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { AddIcon } from "../../common/Components/Icons/AddIcon";
import { canCurrentUserUseProductCapture } from "../../helpers/productCaptureEnabled";
import RequestCapturePanel from "./RequestCapturePanel";
import ProcessMediaGrid, { remoteMediaUrls } from "./ProcessMediaGrid";
import ProcessGeoMap, { ProcessGeoLine } from "./ProcessGeoMap";
import { processGeoPins } from "./processGeo";
import notifications from "../../common/Components/Notifications/Notification";
import { removeProductProcessMedia } from "../../services/productCaptureApi";
import { persistableProcessMediaRows } from "./previewMedia";
import { masterIdFromRef } from "../../uploads/productDraftApi";
import { isProductDraftRevisionsEnabled } from "../../uploads/productDraftRevisionsEnabled";
import { isFlexibleProcessWorkflow, matchProcessMasterOption } from "./productProcessMigrationUtils";
import { newProductProcessId } from "./productProcessIdentity";
import { ArtisanOption, postLabel, postsForStep, searchArtisans } from "./artisanSearch";
import {
  getArtisanPortraits,
  getPublicConsent,
  uploadArtisanPortraitMedia,
  type PublicConsent,
} from "../../services/artisanPortraitApi";
import ArtisanPublicConsent from "./ArtisanPublicConsent";
import { assertPhoto } from "../karkhana/people/photo";
import { isMediaOptimizationEnabled } from "../../media/mediaApi";
import HistoricalTaxonomyValues from "../../taxonomy/HistoricalTaxonomyValues";
import { fieldsForProcess, processFieldId } from "../../taxonomy/publishFields";
import TaxonomyFieldRenderer from "../../taxonomy/TaxonomyFieldRenderer";
import type { ProcessFieldMapping, ResolvedFieldConfig, TaxonomyValues } from "../../taxonomy/types";

export type FlexMediaSlot = {
  key: string;
  productProcessId?: string;
  processMasterId?: string;
  title: string;
  subtitle: string;
  text: string;
  artisan: any;
  baseImages: any[];
  taxonomyValues?: TaxonomyValues;
};

export type ProcessMasterOption = { label: string; value: string; description?: string; inhouse?: boolean; taxonomyIds?: string[]; mappings?: ProcessFieldMapping[] };
type Opt = ProcessMasterOption;

type FlexibleMediaProcessesSectionProps = {
  slots: FlexMediaSlot[];
  onSlotsChange: (next: FlexMediaSlot[]) => void;
  onFilesPicked: (slotKey: string, files: File[]) => void;
  designerOptions: Opt[];
  weaverOptions: Opt[];
  dyerOptions: Opt[];
  finisherOptions: Opt[];
  loader: boolean;
  openCreateArtisanModal: () => void;
  onDeletePreview: (slotKey: string, index: number) => void;
  onDeleteMany?: (slotKey: string, indexes: number[]) => void;
  onToggleShow: (slotKey: string, index: number, show: boolean) => void;
  processMasterOptions: Opt[];
  onCreateProcessMaster: (name: string, description: string, inhouse: boolean) => Promise<Opt | null>;
  productId?: string;
  processFields?: ResolvedFieldConfig[];
  /** "steps": compact cards (number, photos, artisan, Ask to capture) that open to the full form — the 5-step editor. */
  variant?: "classic" | "steps";
  /** Step cards that must stay open, e.g. one holding a field that blocks publishing. */
  openKeys?: string[];
  /** The studio whose people the artisan picker searches. */
  organizationId?: string;
};

const initialsOf = (label: string) =>
  label
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

/** The artisan's portrait when the studio has one, else initials (or + when nobody is picked). A link that no longer loads (it lasts an hour) falls back to initials. */
const ArtisanAvatar = ({ label, portrait }: { label?: string; portrait?: string }) => {
  const [failed, setFailed] = useState("");
  return label && portrait && failed !== portrait ? (
    <img className="ps-mk__av is-photo" src={portrait} alt="" onError={() => setFailed(portrait)} />
  ) : (
    <span className={`ps-mk__av${label ? "" : " is-empty"}`} aria-hidden="true">
      {label ? initialsOf(String(label)) : "+"}
    </span>
  );
};

const shownMediaCount = (slot: FlexMediaSlot) => (slot.baseImages || []).filter((m: any) => m && !m._deleted).length;

/** The last row of a long result: there are more people than shown. */
const MORE = "__more";

/**
 * "Who did it": searches the studio's people with a craft post as you type (Karkhana workers
 * included) by name, worker code or phone. People whose post fits the step come first, and a
 * last row says when there are more than shown. Falls back to the lists the page loaded if the
 * search is unavailable. The list opens over the page (above the sticky step footer).
 */
const ArtisanPicker = ({
  value,
  fallback,
  organizationId,
  stepTitle,
  ariaLabel,
  placeholder,
  className,
  onChange,
}: {
  value: any;
  fallback: Opt[];
  organizationId?: string;
  stepTitle?: string;
  ariaLabel?: string;
  placeholder: string;
  className: string;
  onChange: (next: any) => void;
}) => {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const load = (input: string): Promise<any[]> => {
    const text = input.trim().toLowerCase();
    const local = () => fallback.filter((o) => !text || String(o.label).toLowerCase().includes(text));
    return searchArtisans(input, organizationId, postsForStep(stepTitle))
      .then(({ options, total }) => {
        if (!options.length && !text) return local();
        return total > options.length
          ? [
              ...options,
              {
                value: MORE,
                label: `Showing ${options.length} of ${total} — type more of the name or the code`,
                isDisabled: true,
              },
            ]
          : options;
      })
      .catch(() => local());
  };
  return (
    <AsyncSelect
      className={className}
      classNamePrefix="react-select"
      placeholder={placeholder}
      aria-label={ariaLabel}
      isClearable
      cacheOptions
      defaultOptions
      value={value || null}
      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
      menuPlacement="auto"
      // Drawn over the page, so close it when the page (not the list itself) scrolls.
      closeMenuOnScroll={(e: Event) => !(e.target as Element | null)?.closest?.(".react-select__menu-list")}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 1300 }),
        noOptionsMessage: (base) => ({ ...base, textTransform: "none" }),
      }}
      loadOptions={(input: string) =>
        new Promise<ArtisanOption[]>((resolve) => {
          if (timer.current) clearTimeout(timer.current);
          timer.current = setTimeout(() => resolve(load(input)), input ? 250 : 0);
        })
      }
      noOptionsMessage={({ inputValue }) => (inputValue ? `No one called "${inputValue}"` : "No artisans yet")}
      formatOptionLabel={(o: any, { context }) =>
        o.value === MORE ? (
          <span className="hkp-artisan-more">{o.label}</span>
        ) : context === "menu" && (o.post || o.code) ? (
          <span className="hkp-artisan-opt">
            {o.label}
            <small>{[postLabel(o.post), o.code].filter(Boolean).join(" · ")}</small>
          </span>
        ) : (
          o.label
        )
      }
      onChange={(e: any) => onChange(e && e.value !== MORE ? { label: e.label, value: e.value } : "")}
    />
  );
};

function newFlexKey() {
  return `flex-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export { newFlexKey };

/** Hydrate flexible slots from API (preferred) or legacy four detail blobs. */
export function buildFlexSlotsFromProduct(
  editDetails: any,
  convertMedia: (raw: any) => any[]
): FlexMediaSlot[] {
  if (isFlexibleProcessWorkflow(editDetails)) {
    const rows = Array.isArray(editDetails?.flexibleUploadProcesses)
      ? editDetails.flexibleUploadProcesses
      : [];
    return rows.map((p: any, index: number) => {
      const productProcessId = String(p?.productProcessId || "").trim();
      const processMasterId = String(p?.processMasterId || "").trim();
      const artisanId = masterIdFromRef(p.karigar);
      return {
        key: productProcessId || `flex-${index}`,
        productProcessId: productProcessId || undefined,
        processMasterId: processMasterId || undefined,
        title: p.title ?? "",
        subtitle: p.subtitle ?? "",
        text: p.text ?? "",
        artisan:
          p.karigar?.name && artisanId
            ? { label: p.karigar.name, value: artisanId }
            : "",
        baseImages: p.media?.length ? convertMedia(p.media) : [],
        taxonomyValues: p.taxonomyValues && typeof p.taxonomyValues === "object" ? p.taxonomyValues : {},
      };
    });
  }
  const defaults = defaultLegacyFlexSlots();
  const legacies = [
    editDetails?.designingDetails,
    editDetails?.weavingDetails,
    editDetails?.dyingDetails,
    editDetails?.finishingDetails,
  ];
  return defaults.map((s, i) => {
    const legacy = legacies[i];
    return {
      ...s,
      artisan:
        legacy?.karigar?.name && masterIdFromRef(legacy?.karigar)
          ? { label: legacy.karigar.name, value: masterIdFromRef(legacy.karigar) }
          : "",
      baseImages: legacy?.media?.length ? convertMedia(legacy.media) : [],
    };
  });
}

/** When API has no flexibleUploadProcesses yet—seed four steps matching the old wizard order. */
export function defaultLegacyFlexSlots(): FlexMediaSlot[] {
  return [
    { key: newFlexKey(), title: "Designing", subtitle: "", text: "", artisan: "", baseImages: [] },
    { key: newFlexKey(), title: "Weaving", subtitle: "", text: "", artisan: "", baseImages: [] },
    { key: newFlexKey(), title: "Dying", subtitle: "", text: "", artisan: "", baseImages: [] },
    { key: newFlexKey(), title: "Finishing", subtitle: "", text: "", artisan: "", baseImages: [] },
  ];
}

export function addFlexibleProcessSlot(slots: FlexMediaSlot[]): FlexMediaSlot[] {
  const productProcessId = newProductProcessId();
  return [
    ...(slots || []),
    {
      key: productProcessId,
      productProcessId,
      title: "",
      subtitle: "",
      text: "",
      artisan: "",
      baseImages: [],
    },
  ];
}

export function applyProcessMasterSelection(
  slot: FlexMediaSlot,
  option: { label?: string; value?: string; description?: string } | null
): Partial<FlexMediaSlot> {
  return {
    title: option?.label ? String(option.label) : "",
    processMasterId: option?.value ? String(option.value) : undefined,
    subtitle: option?.description ? String(option.description) : slot.subtitle,
  };
}

export function toProcessMasterOption(item: any, mappings?: ProcessFieldMapping[]): Opt {
  const label = String(item?.name || item?.label || "").trim();
  return {
    label,
    value: masterIdFromRef(item) || label,
    description: item?.description || "",
    inhouse: item?.inhouse === false ? false : true,
    taxonomyIds: Array.isArray(item?.taxonomyIds) ? item.taxonomyIds : [],
    mappings,
  };
}

export function flexSlotsToPayloadProcesses(slots: FlexMediaSlot[]) {
  return slots.map((s) => ({
    productProcessId: s.productProcessId || undefined,
    processMasterId: s.processMasterId || undefined,
    title: String(s.title ?? "").trim(),
    subtitle: String(s.subtitle ?? "").trim(),
    text: String(s.text ?? "").trim(),
    karigarId: masterIdFromRef(s.artisan),
    media: persistableProcessMediaRows(s.baseImages),
    taxonomyValues: s.taxonomyValues || {},
  }));
}

export function FlexibleMediaProcessesSection(props: FlexibleMediaProcessesSectionProps) {
  const {
    slots,
    onSlotsChange,
    onFilesPicked,
    designerOptions,
    weaverOptions,
    dyerOptions,
    finisherOptions,
    loader,
    openCreateArtisanModal,
    onDeletePreview,
    onDeleteMany,
    onToggleShow,
    processMasterOptions,
    onCreateProcessMaster,
    productId,
    processFields = [],
    variant = "classic",
    openKeys = [],
    organizationId,
  } = props;
  const asSteps = variant === "steps";
  const [openKey, setOpenKey] = useState<string | null>(null);

  // Team-only portraits of the picked artisans; "Take portrait" fills one in when it's missing.
  const [portraits, setPortraits] = useState<Record<string, string>>({});
  const [portraitBusy, setPortraitBusy] = useState<string | null>(null);
  const portraitInput = useRef<HTMLInputElement | null>(null);
  const portraitFor = useRef<string>("");
  // Faces only go to the private media bucket; without the media service there is no upload.
  const portraitsOn = isMediaOptimizationEnabled();
  const artisanIds = Array.from(new Set((slots || []).map((s) => masterIdFromRef(s.artisan)).filter(Boolean) as string[]))
    .sort()
    .join(",");
  // Whether each artisan agreed to be shown on certificates; hidden while the server has it off.
  const [consents, setConsents] = useState<Record<string, PublicConsent>>({});
  const [consentOn, setConsentOn] = useState(false);
  useEffect(() => {
    if (!asSteps || !artisanIds) return undefined;
    let live = true;
    getArtisanPortraits(artisanIds.split(","))
      .then((found) => {
        if (live) setPortraits((current) => ({ ...current, ...found }));
      })
      .catch(() => undefined);
    if (portraitsOn) {
      getPublicConsent(artisanIds.split(","))
        .then((found) => {
          if (!live) return;
          setConsentOn(found.enabled);
          setConsents((current) => ({ ...current, ...found.artisans }));
        })
        .catch(() => undefined);
    }
    return () => {
      live = false;
    };
  }, [asSteps, artisanIds, portraitsOn]);
  const consentChanged = (userId: string, consent: PublicConsent) =>
    setConsents((current) => ({ ...current, [userId]: consent }));
  const takePortrait = (userId: string) => {
    portraitFor.current = userId;
    portraitInput.current?.click();
  };
  const savePortrait = async (file?: File) => {
    const userId = portraitFor.current;
    if (!file || !userId) return;
    setPortraitBusy(userId);
    try {
      assertPhoto(file);
      const url = await uploadArtisanPortraitMedia(userId, file);
      const saved = url;
      if (saved) setPortraits((current) => ({ ...current, [userId]: saved }));
      notifications({ title: "Portrait saved", message: "Only your team sees it.", type: "success" });
    } catch (e: any) {
      notifications({ title: "Portrait not saved", message: e?.message || "Try again", type: "danger" });
    } finally {
      setPortraitBusy(null);
    }
  };

  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const combinedArtisanOptions = useMemo(() => {
    const m = new Map<string, Opt>();
    [...designerOptions, ...weaverOptions, ...dyerOptions, ...finisherOptions].forEach((o: Opt) => {
      if (o && o.value) {
        m.set(String(o.value), o);
      }
    });
    return Array.from(m.values());
  }, [designerOptions, weaverOptions, dyerOptions, finisherOptions]);

  const updateSlot = (key: string, patch: Partial<FlexMediaSlot>) => {
    onSlotsChange(slots.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  };

  const slotsRef = useRef(slots);
  slotsRef.current = slots;
  /** Moves one step to a new position without a message (used while dragging). */
  const moveTo = (from: number, to: number) => {
    const current = slotsRef.current;
    if (from === to || to < 0 || to >= current.length) return;
    const next = [...current];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    slotsRef.current = next;
    // Render each step of a drag at once so the next pointer move measures the new order.
    flushSync(() => onSlotsChange(next));
  };

  const move = (index: number, delta: number) => {
    const j = index + delta;
    if (j < 0 || j >= slots.length) return;
    const next = [...slots];
    const t = next[index];
    next[index] = next[j];
    next[j] = t;
    onSlotsChange(next);
    const steps = Math.abs(delta);
    notifications({
      title: "Process order",
      message: `Moved ${steps} step${steps === 1 ? "" : "s"} ${delta < 0 ? "up" : "down"} · now ${j + 1} of ${next.length}`,
      type: "success",
      duration: 2500,
    });
  };

  const removeSlot = (key: string) => {
    if (slots.length <= 1) return;
    const allow = window.confirm("Remove this process step?");
    if (!allow) return;
    onSlotsChange(slots.filter((s) => s.key !== key));
  };

  const addSlot = () => {
    onSlotsChange(addFlexibleProcessSlot(slots));
  };

  const triggerPick = (slotKey: string) => {
    inputRefs.current[slotKey]?.click();
  };

  const addProcessMasterToSlot = async (slot: FlexMediaSlot) => {
    const rawName = window.prompt("New process name");
    if (!rawName || !rawName.trim()) return;
    const rawDescription = window.prompt("Small subtitle/description (optional)") || "";
    const inhouse = window.confirm("Is this process inhouse?\nOK = Inhouse, Cancel = Outsourced");
    const created = await onCreateProcessMaster(rawName, rawDescription, inhouse);
    if (created) updateSlot(slot.key, applyProcessMasterSelection(slot, created));
  };

  const applyLocalDelete = (slotKey: string, indexes: number[]) => {
    if (onDeleteMany) {
      onDeleteMany(slotKey, indexes);
      return;
    }
    [...indexes]
      .sort((a, b) => b - a)
      .forEach((i) => onDeletePreview(slotKey, i));
  };

  const handleDeleteMany = async (slot: FlexMediaSlot, slotIndex: number, indexes: number[]) => {
    if (isProductDraftRevisionsEnabled()) {
      applyLocalDelete(slot.key, indexes);
      return;
    }
    const urls = remoteMediaUrls(slot.baseImages || [], indexes);
    const saved = String(productId || "").trim();
    if (saved && urls.length) {
      const res: any = await removeProductProcessMedia({
        productId: saved,
        processTitle: slot.title,
        processIndex: slotIndex,
        productProcessId: slot.productProcessId,
        mediaUrls: urls,
      });
      if (!res?.success) {
        notifications({
          title: "Error",
          message: `${res?.message || "Could not delete on the server."} Click Save to keep this removal.`,
          type: "danger",
        });
      } else {
        notifications({
          title: "Success",
          message: "Selected photos and videos were removed.",
          type: "success",
        });
      }
    }
    applyLocalDelete(slot.key, indexes);
  };

  // ── "steps" variant: the Making step of the product editor (design P11) ──
  const listRef = useRef<HTMLDivElement | null>(null);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [changingType, setChangingType] = useState<Record<string, boolean>>({});
  const canCapture = canCurrentUserUseProductCapture() && Boolean(String(productId || "").trim());

  const openCaptureFor = (slotKey: string) => {
    setOpenKey(slotKey);
    // Open the card and go straight to its capture link sheet.
    window.setTimeout(() => {
      const open = document.querySelector<HTMLButtonElement>(`[data-mk="${slotKey}"] .hkp-request-capture__open`);
      if (open && !open.disabled) open.click();
      else document.querySelector(`[data-mk="${slotKey}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  /** Drag the ⋮⋮ handle (mouse or finger) to reorder; the list reorders live and says where it landed. */
  const startDrag = (event: React.PointerEvent<HTMLButtonElement>, slotKey: string) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.preventDefault();
    const handle = event.currentTarget;
    try {
      // Stops the browser treating the drag as a scroll or text selection.
      handle.setPointerCapture?.(event.pointerId);
    } catch {
      // Some pointers can't be captured; the window listeners below still follow the drag.
    }
    const startIndex = slotsRef.current.findIndex((s) => s.key === slotKey);
    setDragKey(slotKey);
    const onMove = (e: PointerEvent) => {
      const cards = Array.from(listRef.current?.querySelectorAll<HTMLElement>("[data-mk-card]") || []);
      const from = slotsRef.current.findIndex((s) => s.key === slotKey);
      let to = 0;
      cards.forEach((card, i) => {
        if (card.dataset.mkCard === slotKey) return;
        const r = card.getBoundingClientRect();
        if (e.clientY > r.top + r.height / 2) to = i < from ? to + 1 : i;
      });
      to = Math.max(0, Math.min(slotsRef.current.length - 1, to));
      if (to !== from) moveTo(from, to);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      setDragKey(null);
      const end = slotsRef.current.findIndex((s) => s.key === slotKey);
      if (end !== startIndex) {
        notifications({
          title: "Process order",
          message: `Moved to step ${end + 1} of ${slotsRef.current.length}`,
          type: "success",
          duration: 2500,
        });
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  if (asSteps) {
    return (
      <div className="ps-making" id="hkp-upload-process" ref={listRef}>
        {/* Rear camera: someone in the studio photographs the artisan. */}
        <input
          ref={portraitInput}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => {
            void savePortrait(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        {slots.map((slot, index) => {
          const open = openKey === slot.key || openKeys.includes(slot.key);
          const count = shownMediaCount(slot);
          const title = slot.title || "New step";
          const askFirst = canCapture && count === 0;
          const selectedMaster =
            (slot.processMasterId ? processMasterOptions.find((opt) => opt.value === slot.processMasterId) : null) ||
            matchProcessMasterOption(processMasterOptions, slot.title);
          const showType = !slot.title || changingType[slot.key];
          const fields = fieldsForProcess(slot, processFields, processMasterOptions);
          return (
            <div
              key={slot.key}
              data-mk-card={slot.key}
              className={`ps-mk${open ? " is-open" : ""}${askFirst ? " has-ask" : ""}${dragKey === slot.key ? " is-dragging" : ""}`}
            >
              <div className="ps-mk__head">
                <button
                  type="button"
                  className="ps-mk__grip"
                  aria-label={`Reorder ${title}: drag, or press the up and down arrow keys`}
                  onPointerDown={(event) => startDrag(event, slot.key)}
                  onKeyDown={(event) => {
                    if (event.key === "ArrowUp") {
                      event.preventDefault();
                      move(index, -1);
                    } else if (event.key === "ArrowDown") {
                      event.preventDefault();
                      move(index, 1);
                    }
                  }}
                >
                  <GripIcon />
                </button>
                <button
                  type="button"
                  className="ps-mk__main"
                  aria-expanded={open}
                  onClick={() => setOpenKey(open ? null : slot.key)}
                >
                  <span className="ps-mk__title">
                    <span className="ps-mk__name">
                      {index + 1}. {title}
                    </span>
                    {count ? (
                      <span className="ps-mk__badge is-ok">
                        {count} photo{count === 1 ? "" : "s"}
                      </span>
                    ) : (
                      <span className="ps-mk__badge">No photos yet</span>
                    )}
                  </span>
                  <span className="ps-mk__who">
                    <ArtisanAvatar label={slot.artisan?.label} portrait={portraits[masterIdFromRef(slot.artisan)]} />
                    <span className="ps-mk__whotext">
                      <b>{slot.artisan?.label || "Pick artisan"}</b>
                      <small>{slot.subtitle || "Artisan"}</small>
                    </span>
                  </span>
                </button>
                {askFirst ? (
                  <button type="button" className="ps-btn ps-btn--line ps-btn--sm ps-mk__act" onClick={() => openCaptureFor(slot.key)}>
                    <CameraIcon />
                    <span>Ask to capture</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="ps-btn ps-btn--line ps-btn--sm ps-mk__act"
                    aria-label={`Add photos to ${title}`}
                    onClick={() => triggerPick(slot.key)}
                  >
                    <CameraIcon />
                    <span>Add</span>
                  </button>
                )}
                <div className="ps-mk__geo">
                  <ProcessGeoLine media={slot.baseImages} />
                </div>
              </div>
              <input
                type="file"
                multiple
                accept=".png,.jpg,.jpeg,.heic,.heif,.webp,.avif,.bmp,.mp4,.m4v,.mov,.webm,.ogg,video/quicktime,.pdf"
                hidden
                ref={(el) => {
                  inputRefs.current[slot.key] = el;
                }}
                onChange={(e) => {
                  const fl = e.target.files;
                  if (fl?.length) onFilesPicked(slot.key, Array.prototype.slice.call(fl));
                  e.target.value = "";
                }}
              />
              <div className="ps-mk__body" data-mk={slot.key} hidden={!open}>
                <div className="ps-mk__sec">
                  <div className="ps-mk__label">
                    <span>Photos &amp; videos</span>
                    <small>buyers see these on the certificate</small>
                  </div>
                  <ProcessMediaGrid
                    items={slot.baseImages}
                    loader={loader}
                    onAdd={() => triggerPick(slot.key)}
                    onDeleteMany={(indexes) => handleDeleteMany(slot, index, indexes)}
                    onToggleShow={(i, show) => onToggleShow(slot.key, i, show)}
                    downloadName={slot.title || "process"}
                  />
                </div>
                <div className="ps-mk__grid">
                  <div className="ps-mk__sec">
                    <div className="ps-mk__label">
                      <span>Who did it</span>
                      <button type="button" className="ps-mk__link" onClick={openCreateArtisanModal}>
                        + New artisan
                      </button>
                    </div>
                    <ArtisanPicker
                      className="React_selector"
                      placeholder="Pick or search artisan"
                      ariaLabel={`Artisan for ${title}`}
                      value={slot.artisan}
                      fallback={combinedArtisanOptions}
                      organizationId={organizationId}
                      stepTitle={slot.title}
                      onChange={(artisan) => updateSlot(slot.key, { artisan })}
                    />
                    {masterIdFromRef(slot.artisan) ? (
                      <div className="ps-mk__portrait">
                        <ArtisanAvatar label={slot.artisan?.label} portrait={portraits[masterIdFromRef(slot.artisan)]} />
                        <span className="ps-mk__portrait-text">
                          {portraits[masterIdFromRef(slot.artisan)] ? "Portrait" : "No portrait yet"}
                          <small>
                            {!portraitsOn
                              ? "Portraits need private photo storage, not on yet"
                              : consentOn &&
                                  portraits[masterIdFromRef(slot.artisan)] &&
                                  consents[String(masterIdFromRef(slot.artisan))]?.status === "GRANTED"
                                ? "Shown on your certificates"
                                : "Only your team sees it"}
                          </small>
                        </span>
                        {portraitsOn ? (
                        <button
                          type="button"
                          className="ps-btn ps-btn--line ps-btn--sm"
                          disabled={portraitBusy === masterIdFromRef(slot.artisan)}
                          onClick={() => takePortrait(String(masterIdFromRef(slot.artisan)))}
                        >
                          <CameraIcon />
                          <span>
                            {portraitBusy === masterIdFromRef(slot.artisan)
                              ? "Saving…"
                              : portraits[masterIdFromRef(slot.artisan)]
                                ? "Retake"
                                : "Take portrait"}
                          </span>
                        </button>
                        ) : null}
                      </div>
                    ) : null}
                    {portraitsOn && consentOn && masterIdFromRef(slot.artisan) ? (
                      <ArtisanPublicConsent
                        userId={String(masterIdFromRef(slot.artisan))}
                        name={slot.artisan?.label}
                        hasPhoto={Boolean(portraits[masterIdFromRef(slot.artisan)])}
                        consent={consents[String(masterIdFromRef(slot.artisan))]}
                        onChange={consentChanged}
                      />
                    ) : null}
                  </div>
                  {showType ? (
                    <div className="ps-mk__sec">
                      <div className="ps-mk__label">
                        <span>Step</span>
                        <button type="button" className="ps-mk__link" onClick={() => void addProcessMasterToSlot(slot)}>
                          + New step type
                        </button>
                      </div>
                      <Select
                        className="React_selector"
                        classNamePrefix="react-select"
                        placeholder="Designing, weaving, dyeing…"
                        aria-label={`Step type for ${title}`}
                        isSearchable
                        value={selectedMaster}
                        options={processMasterOptions}
                        onChange={(e: any) => {
                          updateSlot(slot.key, applyProcessMasterSelection(slot, e));
                          setChangingType((prev) => ({ ...prev, [slot.key]: false }));
                        }}
                      />
                    </div>
                  ) : null}
                </div>
                <div className="ps-mk__sec">
                  <div className="ps-mk__label">
                    <span>What happens in this step</span>
                    <small>buyers see this</small>
                  </div>
                  <Input
                    type="textarea"
                    rows={2}
                    aria-label={`What happens in ${title}`}
                    placeholder="e.g. Hand-drawn naksha on graph paper, then punched cards for the loom"
                    value={slot.text}
                    onChange={(e) => updateSlot(slot.key, { text: e.target.value })}
                  />
                </div>
                {fields.length ? (
                  <div className="ps-mk__sec">
                    <Row>
                      {fields.map((field) => (
                        <Col xs={12} md={6} key={field.taxonomyId} className="mb-2">
                          <TaxonomyFieldRenderer
                            id={processFieldId(slot.key, field.taxonomyId)}
                            field={field}
                            value={slot.taxonomyValues?.[field.taxonomyId]}
                            productId={productId}
                            onChange={(taxonomyId, value) =>
                              updateSlot(slot.key, {
                                taxonomyValues: { ...(slot.taxonomyValues || {}), [taxonomyId]: value },
                              })
                            }
                          />
                        </Col>
                      ))}
                    </Row>
                  </div>
                ) : null}
                {/* Saved values stay visible (provenance) even when no field is active for this step any more. */}
                <HistoricalTaxonomyValues values={slot.taxonomyValues} fields={fields} />
                <div className="ps-mk__foot">
                  <RequestCapturePanel
                    key={slot.productProcessId || slot.key}
                    productId={productId}
                    processTitle={slot.title}
                    processIndex={index}
                    productProcessId={slot.productProcessId}
                  />
                  <span className="ps-mk__spacer" />
                  {!showType ? (
                    <button
                      type="button"
                      className="ps-mk__link"
                      onClick={() => setChangingType((prev) => ({ ...prev, [slot.key]: true }))}
                    >
                      Change step type
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="ps-mk__link is-danger"
                    disabled={slots.length <= 1}
                    onClick={() => removeSlot(slot.key)}
                  >
                    Remove step
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        <div className="ps-mk__add">
          <button
            type="button"
            className="ps-btn ps-btn--line"
            onClick={() => {
              // Open the new step: choosing its type is the next thing to do.
              const next = addFlexibleProcessSlot(slots);
              onSlotsChange(next);
              setOpenKey(next[next.length - 1]?.key || null);
            }}
          >
            <span>+ Add step</span>
          </button>
          <small>Drag ⋮⋮ to reorder</small>
        </div>
        <ProcessGeoMap pins={processGeoPins(slots.map((slot) => ({ title: slot.title, media: slot.baseImages })))} />
      </div>
    );
  }

  return (
    <div className={asSteps ? "ps-making" : "UserDetailsDiv mb-5"} id="hkp-upload-process">
      {asSteps ? null : (
      <div className="UserHeader headerDisplay mb-2">
        <div>Upload process (flexible)</div>
      </div>
      )}
      {asSteps ? null : (
      <p className="text-muted small px-2 mb-3">
        Add as many steps as you need. Rename each process, add a public description, optionally
        choose an artisan, and upload files. Use Create capture link on a saved product to send a
        time-limited camera link for that step.
      </p>
      )}
      {canCurrentUserUseProductCapture() && !String(productId || "").trim() ? (
        <div className="hkp-request-capture mx-2 mb-3">
          Save this product first. Then open View or Edit and generate a live capture link on each
          process.
        </div>
      ) : null}
      {slots.map((slot, index) => (
        <div
          key={slot.key}
          className={asSteps ? `ps-mk${openKey === slot.key || openKeys.includes(slot.key) ? " is-open" : ""}` : "userPd border rounded p-2 mb-3 bg-white hkp-process-card"}
        >
          {asSteps ? (
            <div className="ps-mk__head">
              <div className="ps-mk__move">
                <button type="button" aria-label="Move up" disabled={index === 0} onClick={() => move(index, -1)}>
                  <FiChevronUp />
                </button>
                <button type="button" aria-label="Move down" disabled={index >= slots.length - 1} onClick={() => move(index, 1)}>
                  <FiChevronDown />
                </button>
              </div>
              <button
                type="button"
                className="ps-mk__main"
                aria-expanded={openKey === slot.key}
                onClick={() => setOpenKey(openKey === slot.key ? null : slot.key)}
              >
                <span className="ps-mk__title">
                  {index + 1}. {slot.title || "New step"}
                  {shownMediaCount(slot) ? (
                    <span className="ps-mk__badge is-ok">
                      {shownMediaCount(slot)} photo{shownMediaCount(slot) === 1 ? "" : "s"}
                    </span>
                  ) : (
                    <span className="ps-mk__badge">No photos yet</span>
                  )}
                </span>
                <span className="ps-mk__who">
                  <ArtisanAvatar label={slot.artisan?.label} portrait={portraits[masterIdFromRef(slot.artisan)]} />
                  <span>
                    <b>{slot.artisan?.label || "Pick artisan"}</b>
                    <small>{slot.subtitle || "tap to add details, photos and capture link"}</small>
                  </span>
                </span>
              </button>
              <button
                type="button"
                className="ps-btn ps-btn--line ps-btn--sm ps-mk__act"
                onClick={() => {
                  if (canCurrentUserUseProductCapture() && String(productId || "").trim()) {
                    setOpenKey(slot.key);
                    // Open the card and go straight to its capture link sheet.
                    window.setTimeout(() => {
                      const open = document.querySelector<HTMLButtonElement>(`[data-mk="${slot.key}"] .hkp-request-capture__open`);
                      if (open && !open.disabled) open.click();
                      else document.querySelector(`[data-mk="${slot.key}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
                    }, 50);
                  } else {
                    triggerPick(slot.key);
                  }
                }}
              >
                <span>{canCurrentUserUseProductCapture() && String(productId || "").trim() ? "Ask to capture" : "Add photos"}</span>
              </button>
              <div className="ps-mk__geo">
                <ProcessGeoLine media={slot.baseImages} />
              </div>
            </div>
          ) : null}
          <div className="ps-mk__body" data-mk={slot.key} hidden={asSteps && openKey !== slot.key && !openKeys.includes(slot.key)}>
          <Row>
            <Col xs={12}>
          {(() => {
            const selectedMaster =
              (slot.processMasterId
                ? processMasterOptions.find((opt) => opt.value === slot.processMasterId)
                : null) || matchProcessMasterOption(processMasterOptions, slot.title);
            return (
              <Row className="mb-2 align-items-end">
                <Col xs={6}>
                  <Label>Process name</Label>
                  <div className="formDivSub hkp-process-name">
                    <Select
                      className="React_selector text-white"
                      classNamePrefix="react-select"
                      placeholder="Select"
                      isSearchable
                      value={selectedMaster}
                      options={processMasterOptions}
                      onChange={(e: any) => {
                        updateSlot(slot.key, applyProcessMasterSelection(slot, e));
                      }}
                    />
                    <button type="button" className="formAddDiv" aria-label="Add process" onClick={() => void addProcessMasterToSlot(slot)}>
                      <AddIcon />
                    </button>
                  </div>
                </Col>
                <Col xs={6}>
                  <Label>Artisan (optional)</Label>
                  <div className="formDivSub">
                    <ArtisanPicker
                      className="React_selector text-white"
                      placeholder="Select"
                      value={slot.artisan}
                      fallback={combinedArtisanOptions}
                      organizationId={organizationId}
                      stepTitle={slot.title}
                      onChange={(artisan) => updateSlot(slot.key, { artisan })}
                    />
                    <button type="button" className="formAddDiv" aria-label="Add artisan" onClick={openCreateArtisanModal}>
                      <AddIcon />
                    </button>
                  </div>
                </Col>
              </Row>
            );
          })()}
          {(() => {
            const fields = fieldsForProcess(slot, processFields, processMasterOptions);
            return (
              <div>
              <Row className="mb-2">
                {fields.map((field) => (
                  <Col xs={12} md={6} key={field.taxonomyId} className="mb-2">
                    <TaxonomyFieldRenderer
                      id={processFieldId(slot.key, field.taxonomyId)}
                      field={field}
                      value={slot.taxonomyValues?.[field.taxonomyId]}
                      productId={productId}
                      onChange={(taxonomyId, value) =>
                        updateSlot(slot.key, {
                          taxonomyValues: { ...(slot.taxonomyValues || {}), [taxonomyId]: value },
                        })
                      }
                    />
                  </Col>
                ))}
              </Row>
              <HistoricalTaxonomyValues values={slot.taxonomyValues} fields={fields} />
              </div>
            );
          })()}
          <Row className="mb-3">
            <Col xs={12}>
              {slot.subtitle ? (
                <p className="text-muted small mb-1">{slot.subtitle}</p>
              ) : null}
            </Col>
            <Col xs={12}>
              <Label>Process details (public)</Label>
              <Input
                type="textarea"
                rows={2}
                placeholder="Describe what happens in this process step."
                value={slot.text}
                onChange={(e) => updateSlot(slot.key, { text: e.target.value })}
              />
            </Col>
          </Row>
          <Label>Upload files</Label>
          <span className="ms-2 text-muted small">
            Photo (jpg, jpeg, png) / Video (mp4, ogg, webm) / PDF. Tick a box to delete.
          </span>
          <input
            type="file"
            multiple
            accept=".png,.jpg,.jpeg,.doc,.docx,.pdf,.mp4,.webm,.ogg,.heic,.bmp"
            style={{ display: "none" }}
            ref={(el) => {
              inputRefs.current[slot.key] = el;
            }}
            onChange={(e) => {
              const fl = e.target.files;
              if (fl?.length) {
                onFilesPicked(slot.key, Array.prototype.slice.call(fl));
              }
              e.target.value = "";
            }}
          />
          <div className="hkp-process-flow mt-2">
            <RequestCapturePanel
              key={slot.productProcessId || slot.key}
              productId={productId}
              processTitle={slot.title}
              processIndex={index}
              productProcessId={slot.productProcessId}
            />
            <div className="hkp-process-actions">
              <Button
                type="button"
                size="sm"
                color="secondary"
                outline
                className="hkp-process-actions__icon"
                disabled={index === 0}
                aria-label="Move up"
                title="Move up"
                onClick={() => move(index, -1)}
              >
                <FiChevronUp />
              </Button>
              <Button
                type="button"
                size="sm"
                color="secondary"
                outline
                className="hkp-process-actions__icon"
                disabled={index >= slots.length - 1}
                aria-label="Move down"
                title="Move down"
                onClick={() => move(index, 1)}
              >
                <FiChevronDown />
              </Button>
              <Button
                type="button"
                size="sm"
                color="danger"
                outline
                className="hkp-process-actions__icon"
                disabled={slots.length <= 1}
                aria-label="Remove step"
                title="Remove step"
                onClick={() => removeSlot(slot.key)}
              >
                <FiTrash2 />
              </Button>
            </div>
            <ProcessMediaGrid
              items={slot.baseImages}
              loader={loader}
              onAdd={() => triggerPick(slot.key)}
              onDeleteMany={(indexes) => handleDeleteMany(slot, index, indexes)}
              onToggleShow={(i, show) => onToggleShow(slot.key, i, show)}
              downloadName={slot.title || "process"}
            />
            <ProcessGeoLine media={slot.baseImages} />
          </div>
            </Col>
          </Row>
          </div>
        </div>
      ))}
      {asSteps ? (
        <div className="ps-mk__add">
          <button type="button" className="ps-btn ps-btn--line" onClick={addSlot}>
            <span>+ Add step</span>
          </button>
          <small>Use ↑ ↓ to reorder</small>
        </div>
      ) : (
      <div className="hkp-process-add px-2 mb-3">
        <Button color="primary" outline size="sm" onClick={addSlot}>
          Add process step
        </Button>
      </div>
      )}
      <ProcessGeoMap
        pins={processGeoPins(
          slots.map((slot) => ({ title: slot.title, media: slot.baseImages }))
        )}
      />
    </div>
  );
}

const GripIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    {[6, 12, 18].flatMap((y) => [9, 15].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" />))}
  </svg>
);

const CameraIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
    <circle cx="12" cy="13" r="3.5" />
  </svg>
);

